import React from 'react';
import {
  View,
  ScrollView,
  ActivityIndicator,
  TouchableOpacity,
} from 'react-native';
import Text from 'app/components/Text';
import TextInput from 'app/components/TextInput';
import journalChatActions from 'app/utils/journalChatActions';
import clipboardHelpers from 'app/utils/clipboardHelpers';
import { StyleContext } from 'app/contexts/Style';
import { Feather } from '@expo/vector-icons';
import MomentBullet from 'app/components/chapter/MomentBullet';

export default class JournalChatPanel extends React.Component {
  static contextType = StyleContext;

  state = {
    messages: [],
    loading: false,
    inputText: '',
    useClaude: false,
    error: null,
  };

  // entryRefs[messageIdx][entryIdx] = MomentBullet ref
  entryRefs = {};

  generate = async (followUp = null) => {
    const { currentText, currentTitle, currentValueId } = this.props;
    const { messages, useClaude, inputText } = this.state;

    // First send: use inputText as the initial prompt/context if provided
    const isFirstMessage = messages.length === 0;
    const prompt = followUp ?? (isFirstMessage ? inputText.trim() || null : null);

    const conversationHistory = !isFirstMessage
      ? messages.map((m) => ({
          role: m.role,
          content: m.role === 'user' ? m.content : m.reflection,
        }))
      : [];

    this.setState((s) => ({
      loading: true,
      error: null,
      inputText: '',
      messages: prompt
        ? [...s.messages, { role: 'user', content: prompt }]
        : s.messages,
    }));

    const res = await journalChatActions.generate(
      currentText, currentTitle, currentValueId, conversationHistory, prompt, useClaude
    );

    if (res?.success) {
      const raw = JSON.stringify({
        reflection: res.reflection,
        referenced_entries: res.referenced_entries,
      });
      this.setState((s) => ({
        loading: false,
        messages: [
          ...s.messages,
          {
            role: 'assistant',
            reflection: res.reflection,
            referencedEntries: res.referenced_entries || [],
            raw,
          },
        ],
      }));
      this.scrollView?.scrollToEnd?.({ animated: true });
    } else {
      this.setState({ loading: false, error: res?.error || 'Something went wrong.' });
    }
  };

  copyToClipboard = () => {
    const { messages, useClaude } = this.state;
    const model = useClaude ? 'Claude' : 'Ollama';
    const blocks = [`Reflections from ${model}`];

    for (const m of messages) {
      if (m.role === 'user') {
        blocks.push(`> ${m.content}`);
      } else {
        const entries = m.referencedEntries || [];
        const citations = entries.length > 0
          ? `\n\n### References\n${entries.map((e) => `{${e.id}}`).join('\n')}`
          : '';
        blocks.push(m.reflection + citations);
      }
    }

    clipboardHelpers.copy(blocks.join('\n\n---\n\n'));
  };

  getEntryRef = (msgIdx, entryIdx) => {
    if (!this.entryRefs[msgIdx]) this.entryRefs[msgIdx] = {};
    if (!this.entryRefs[msgIdx][entryIdx]) this.entryRefs[msgIdx][entryIdx] = React.createRef();
    return this.entryRefs[msgIdx][entryIdx];
  };

  renderEntry = (entry, entryIdx, totalEntries, msgIdx) => {
    const ref = this.getEntryRef(msgIdx, entryIdx);
    const moment = {
      id: entry.id,
      value_id: entry.id,
      value: {
        id: entry.id,
        title: entry.title || entry.date,
        text: entry.text,
        created_at: entry.date,
        color: entry.color,
        icon: entry.icon,
      },
    };
    return (
      <View key={entry.id} style={{ marginLeft: -4 }}>
        <MomentBullet
          ref={ref}
          moment={moment}
          pinnedMomentList
          focusPrevious={entryIdx > 0 ? () => {
            this.entryRefs[msgIdx]?.[entryIdx - 1]?.current?.focus?.();
          } : undefined}
          focusNext={entryIdx < totalEntries - 1 ? () => {
            this.entryRefs[msgIdx]?.[entryIdx + 1]?.current?.focus?.();
          } : undefined}
        />
      </View>
    );
  };

  renderMessage = (message, i) => {
    const { colors, sizes } = this.context;
    if (message.role === 'user') {
      return (
        <View
          key={i}
          style={{
            alignSelf: 'flex-end',
            backgroundColor: colors.trim,
            borderRadius: 10,
            paddingHorizontal: 10,
            paddingVertical: 6,
            marginBottom: 10,
            maxWidth: '85%',
          }}
        >
          <Text style={{ color: colors.foreground, fontSize: sizes.default, lineHeight: sizes.default * 1.5 }}>
            {message.content}
          </Text>
        </View>
      );
    }

    const entries = message.referencedEntries || [];
    return (
      <View key={i} style={{ marginBottom: 12 }}>
        <Text style={{ color: colors.foreground, fontSize: sizes.default, lineHeight: sizes.default * 1.7, marginBottom: 8 }}>
          {message.reflection}
        </Text>
        {entries.length > 0 && (
          <View style={{ marginTop: 4 }}>
            {entries.map((entry, ei) => this.renderEntry(entry, ei, entries.length, i))}
          </View>
        )}
      </View>
    );
  };

  render() {
    const { onClose } = this.props;
    const { messages, loading, inputText, useClaude, error } = this.state;
    const { colors, sizes } = this.context;
    const hasContent = messages.length > 0 || !!error;
    const isFirstMessage = messages.length === 0 && !loading && !error;

    return (
      <View style={{ borderTopWidth: 1, borderTopColor: colors.trim, marginTop: sizes.base, paddingTop: sizes.base, paddingRight: sizes.base, width: '100%' }}>
        {/* Header */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: sizes.base }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Feather name="message-circle" size={sizes.default} color={colors.placeholder} />
            <Text style={{ color: colors.placeholder, fontSize: sizes.default, fontWeight: '600', letterSpacing: 0.5 }}>
              REFLECT
            </Text>
          </View>
          <View style={{ flexDirection: 'row', gap: 12, alignItems: 'center' }}>
            {hasContent && (
              <TouchableOpacity onPress={this.copyToClipboard}>
                <Feather name="copy" size={sizes.default} color={colors.placeholder} />
              </TouchableOpacity>
            )}
            {!loading && hasContent && (
              <TouchableOpacity onPress={() => this.generate()}>
                <Feather name="refresh-cw" size={sizes.default} color={colors.placeholder} />
              </TouchableOpacity>
            )}
            <TouchableOpacity onPress={onClose}>
              <Feather name="x" size={sizes.default} color={colors.foreground} />
            </TouchableOpacity>
          </View>
        </View>

        {/* Conversation thread */}
        {hasContent && (
          <ScrollView
            ref={(r) => (this.scrollView = r)}
            style={{ maxHeight: 400 }}
            contentContainerStyle={{ paddingBottom: sizes.base }}
            showsVerticalScrollIndicator={false}
          >
            {messages.map((m, i) => this.renderMessage(m, i))}
            {loading && (
              <View style={{ paddingVertical: 12, alignItems: 'flex-start' }}>
                <ActivityIndicator color={colors.placeholder} size="small" />
              </View>
            )}
            {!!error && !loading && (
              <Text style={{ color: colors.foreground, fontSize: sizes.default }}>{error}</Text>
            )}
          </ScrollView>
        )}

        {/* Initial loading (before any content) */}
        {loading && !hasContent && (
          <View style={{ paddingVertical: 16, alignItems: 'flex-start' }}>
            <ActivityIndicator color={colors.placeholder} />
          </View>
        )}

        {/* Input row */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          borderTopWidth: hasContent ? 1 : 0,
          borderTopColor: colors.trim,
          paddingTop: hasContent ? sizes.small : 0,
          gap: 8,
          marginTop: hasContent ? sizes.small : 0,
        }}>
          <TextInput
            value={inputText}
            onChangeText={(t) => this.setState({ inputText: t })}
            placeholder={isFirstMessage ? 'Ask something, or just reflect...' : 'Follow up...'}
            placeholderTextColor={colors.trim}
            style={{
              flex: 1,
              fontSize: sizes.default,
              paddingVertical: 6,
              minHeight: 32,
            }}
            multiline
            onSubmitEditing={() => {
              if (loading) return;
              if (isFirstMessage) {
                this.generate();
              } else if (inputText.trim()) {
                this.generate(inputText.trim());
              }
            }}
            blurOnSubmit={false}
          />

          {/* Claude toggle — only shown before first submission */}
          {isFirstMessage && (
            <TouchableOpacity
              onPress={() => this.setState({ useClaude: !useClaude })}
              onMouseDown={(e) => e?.preventDefault?.()}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}
            >
              <View style={{
                width: 28,
                height: 16,
                borderRadius: 8,
                backgroundColor: useClaude ? colors.active : colors.trim,
                justifyContent: 'center',
                paddingHorizontal: 2,
              }}>
                <View style={{
                  width: 12,
                  height: 12,
                  borderRadius: 6,
                  backgroundColor: '#fff',
                  alignSelf: useClaude ? 'flex-end' : 'flex-start',
                }} />
              </View>
              <Text style={{ color: colors.foreground, fontSize: sizes.default }}>Claude</Text>
            </TouchableOpacity>
          )}

          {/* Send button */}
          <TouchableOpacity
            onPress={() => {
              if (loading) return;
              if (isFirstMessage) {
                this.generate();
              } else if (inputText.trim()) {
                this.generate(inputText.trim());
              }
            }}
            disabled={loading || (!isFirstMessage && !inputText.trim())}
            onMouseDown={(e) => e?.preventDefault?.()}
          >
            <Feather
              name="send"
              size={sizes.default}
              color={(isFirstMessage || inputText.trim()) && !loading ? colors.foreground : colors.placeholder}
            />
          </TouchableOpacity>
        </View>
      </View>
    );
  }
}
